import React, { useState, useEffect } from 'react';
import { 
  Search, 
  Filter, 
  MapPin, 
  Users, 
  DollarSign, 
  Calendar, 
  Clock,
  Lock,
  Globe,
  Star,
  TrendingUp,
  Eye,
  UserPlus,
  X,
  ArrowLeft
} from 'lucide-react';
import NativeAdCard from './NativeAdCard';
import { useNativeAd } from './AdProvider';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Slider } from './ui/slider';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ExploreGroupsPage = ({ user, onJoinGroup, onBack }) => {
  const [allGroups, setAllGroups] = useState([]);
  const [filteredGroups, setFilteredGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [showFilters, setShowFilters] = useState(false);

  // Search and filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    category: '',
    minMembers: [0],
    maxMembers: [100],
    minContribution: [0],
    maxContribution: [10000],
    currency: '',
    status: '',
    privacy: '', // 'public' or 'private'
    region: ''
  });

  useEffect(() => {
    fetchAllGroups();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [searchTerm, filters, allGroups]);

  const fetchAllGroups = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/groups/explore`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        // Filter out groups user is already member of
        const available = data.groups?.filter(group => 
          !group.members?.includes(user.id) && group.id !== user.id
        ) || [];
        setAllGroups(available);
      }
    } catch (error) {
      console.error('Error fetching groups:', error);
      // Fallback to public groups endpoint if explore doesn't exist
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API}/groups/public`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          const available = data.groups?.filter(group => 
            !group.members?.includes(user.id)
          ) || [];
          setAllGroups(available);
        }
      } catch (fallbackError) {
        console.error('Error with fallback:', fallbackError);
      }
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = allGroups;

    // Text search
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(group =>
        group.name.toLowerCase().includes(term) ||
        group.description?.toLowerCase().includes(term) ||
        group.category?.toLowerCase().includes(term)
      );
    }

    // Category filter
    if (filters.category) {
      filtered = filtered.filter(group => 
        group.category === filters.category
      );
    }

    // Member count filter
    filtered = filtered.filter(group => {
      const memberCount = group.members?.length || 0;
      return memberCount >= filters.minMembers[0] && memberCount <= filters.maxMembers[0];
    });

    // Contribution amount filter
    if (filters.minContribution[0] > 0 || filters.maxContribution[0] < 10000) {
      filtered = filtered.filter(group => {
        const contribution = group.contribution_amount || 0;
        return contribution >= filters.minContribution[0] && contribution <= filters.maxContribution[0];
      });
    }

    // Currency filter
    if (filters.currency) {
      filtered = filtered.filter(group => group.currency === filters.currency);
    }

    // Status filter
    if (filters.status) {
      filtered = filtered.filter(group => group.status === filters.status);
    }

    // Privacy filter
    if (filters.privacy) {
      filtered = filtered.filter(group => {
        if (filters.privacy === 'public') return !group.is_private;
        if (filters.privacy === 'private') return group.is_private;
        return true;
      });
    }

    setFilteredGroups(filtered);
  };

  const resetFilters = () => {
    setFilters({
      category: '',
      minMembers: [0],
      maxMembers: [100],
      minContribution: [0],
      maxContribution: [10000],
      currency: '',
      status: '',
      privacy: '',
      region: ''
    });
    setSearchTerm('');
  };

  const getUniqueValues = (key) => {
    const values = allGroups
      .map(group => group[key])
      .filter(value => value && value !== '')
      .filter((value, index, self) => self.indexOf(value) === index);
    return values.sort();
  };

  const handleJoinRequest = async (groupId) => {
    if (onJoinGroup) {
      await onJoinGroup(groupId);
      // Refresh groups to remove the one we just joined
      fetchAllGroups();
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-4 pb-20">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/3"></div>
          <div className="h-12 bg-gray-200 rounded"></div>
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-40 bg-gray-200 rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 pb-20">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <button 
            onClick={onBack}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-800 px-3 py-2 rounded-md hover:bg-gray-100 transition-colors"
            aria-label="Retour"
          >
            <ArrowLeft size={20} />
            <span>Retour</span>
          </button>
        </div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Search className="w-6 h-6 text-blue-600" />
          Explorer les Groupes
        </h1>
        <p className="text-gray-600 mt-1">
          Découvrez et rejoignez des groupes d'épargne
        </p>
      </div>

      {/* Search and Filter Bar */}
      <div className="mb-6 space-y-4">
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            type="text"
            placeholder="Rechercher des groupes par nom, description..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-12"
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="absolute right-3 top-1/2 transform -translate-y-1/2"
            >
              <X className="w-4 h-4 text-gray-400 hover:text-gray-600" />
            </button>
          )}
        </div>

        {/* Filter Toggle */}
        <div className="flex justify-between items-center">
          <Button
            variant="outline"
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2"
          >
            <Filter className="w-4 h-4" />
            Filtres
            {Object.values(filters).some(f => f && f.length && (Array.isArray(f) ? f[0] !== 0 : f !== '')) && (
              <Badge variant="secondary" className="ml-2">Actifs</Badge>
            )}
          </Button>

          <div className="text-sm text-gray-600">
            {filteredGroups.length} groupe{filteredGroups.length !== 1 ? 's' : ''} trouvé{filteredGroups.length !== 1 ? 's' : ''}
          </div>
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <Card>
            <CardContent className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* Category */}
                <div>
                  <Label>Catégorie</Label>
                  <Select value={filters.category} onValueChange={(value) => 
                    setFilters(prev => ({ ...prev, category: value }))
                  }>
                    <SelectTrigger>
                      <SelectValue placeholder="Toutes les catégories" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Toutes les catégories</SelectItem>
                      {getUniqueValues('category').map(category => (
                        <SelectItem key={category} value={category}>
                          {category}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Currency */}
                <div>
                  <Label>Devise</Label>
                  <Select value={filters.currency} onValueChange={(value) => 
                    setFilters(prev => ({ ...prev, currency: value }))
                  }>
                    <SelectTrigger>
                      <SelectValue placeholder="Toutes les devises" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Toutes les devises</SelectItem>
                      {getUniqueValues('currency').map(currency => (
                        <SelectItem key={currency} value={currency}>
                          {currency}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Privacy */}
                <div>
                  <Label>Type</Label>
                  <Select value={filters.privacy} onValueChange={(value) => 
                    setFilters(prev => ({ ...prev, privacy: value }))
                  }>
                    <SelectTrigger>
                      <SelectValue placeholder="Public et Privé" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous types</SelectItem>
                      <SelectItem value="public">Public</SelectItem>
                      <SelectItem value="private">Privé</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Member Count Range */}
                <div>
                  <Label>Nombre de membres: {filters.minMembers[0]} - {filters.maxMembers[0]}</Label>
                  <div className="space-y-2">
                    <div>
                      <Label className="text-xs">Min: {filters.minMembers[0]}</Label>
                      <Slider
                        value={filters.minMembers}
                        onValueChange={(value) => setFilters(prev => ({ ...prev, minMembers: value }))}
                        max={50}
                        step={1}
                        className="w-full"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Max: {filters.maxMembers[0]}</Label>
                      <Slider
                        value={filters.maxMembers}
                        onValueChange={(value) => setFilters(prev => ({ ...prev, maxMembers: value }))}
                        max={100}
                        step={5}
                        className="w-full"
                      />
                    </div>
                  </div>
                </div>

                {/* Contribution Range */}
                <div>
                  <Label>Contribution: {filters.minContribution[0]} - {filters.maxContribution[0]}</Label>
                  <div className="space-y-2">
                    <div>
                      <Label className="text-xs">Min: {filters.minContribution[0]}</Label>
                      <Slider
                        value={filters.minContribution}
                        onValueChange={(value) => setFilters(prev => ({ ...prev, minContribution: value }))}
                        max={5000}
                        step={100}
                        className="w-full"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Max: {filters.maxContribution[0]}</Label>
                      <Slider
                        value={filters.maxContribution}
                        onValueChange={(value) => setFilters(prev => ({ ...prev, maxContribution: value }))}
                        max={20000}
                        step={500}
                        className="w-full"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex gap-2 mt-4">
                <Button onClick={resetFilters} variant="outline" size="sm">
                  Réinitialiser
                </Button>
                <Button onClick={() => setShowFilters(false)} size="sm">
                  Appliquer
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Groups List */}
      <div className="space-y-4">
        {filteredGroups.length === 0 ? (
          <Card>
            <CardContent className="text-center py-12">
              <Search className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {searchTerm || Object.values(filters).some(f => f && f !== '' && (Array.isArray(f) ? f[0] !== 0 : true))
                  ? 'Aucun groupe trouvé'
                  : 'Aucun groupe disponible'
                }
              </h3>
              <p className="text-gray-500 mb-4">
                {searchTerm || Object.values(filters).some(f => f && f !== '' && (Array.isArray(f) ? f[0] !== 0 : true))
                  ? 'Essayez de modifier vos critères de recherche'
                  : 'Il n\'y a pas de groupes publics disponibles pour le moment'
                }
              </p>
              {(searchTerm || Object.values(filters).some(f => f && f !== '')) && (
                <Button onClick={resetFilters} variant="outline">
                  Réinitialiser les filtres
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          filteredGroups.map((group, index) => {
            const memberCount = group.members?.length || 0;
            const contribution = group.contribution_amount || 0;
            const currency = group.currency || 'KES';
            const isPrivate = group.is_private;
            const { shouldShowNative, NativeAdComponent } = useNativeAd(index);
            
            return (
              <React.Fragment key={group.id}>
                {/* Show native ad every 3 groups in explorer */}
                {shouldShowNative && NativeAdComponent && (
                  <div className="my-6">
                    <NativeAdCard
                      adType="groupSuggestion"
                      placement="explore-groups"
                      onAdClick={(adData, source) => {
                        console.log('Native ad clicked in Explorer:', adData);
                        // Handle ad click analytics
                      }}
                      onAdInteraction={(adId, action, value) => {
                        console.log('Native ad interaction:', { adId, action, value });
                      }}
                    />
                  </div>
                )}
                
                <Card className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex items-center gap-3 flex-1">
                      <div className="w-12 h-12 bg-gradient-to-r from-purple-400 to-blue-400 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-bold text-lg">
                          {group.name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="text-lg font-semibold text-gray-900 truncate">
                            {group.name}
                          </h3>
                          {isPrivate ? (
                            <Lock className="w-4 h-4 text-gray-500" title="Groupe privé" />
                          ) : (
                            <Globe className="w-4 h-4 text-green-500" title="Groupe public" />
                          )}
                        </div>
                        <p className="text-sm text-gray-600 line-clamp-2">
                          {group.description || 'Aucune description disponible'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Group Stats */}
                  <div className="grid grid-cols-3 gap-4 mb-4">
                    <div className="text-center">
                      <div className="flex items-center justify-center gap-1 text-blue-600 mb-1">
                        <Users className="w-4 h-4" />
                        <span className="font-bold">{memberCount}</span>
                      </div>
                      <div className="text-xs text-gray-500">Membres</div>
                    </div>
                    <div className="text-center">
                      <div className="flex items-center justify-center gap-1 text-green-600 mb-1">
                        <DollarSign className="w-4 h-4" />
                        <span className="font-bold">{contribution.toLocaleString()}</span>
                      </div>
                      <div className="text-xs text-gray-500">{currency}</div>
                    </div>
                    <div className="text-center">
                      <div className="flex items-center justify-center gap-1 text-purple-600 mb-1">
                        <Calendar className="w-4 h-4" />
                        <span className="font-bold">#{group.current_cycle || 1}</span>
                      </div>
                      <div className="text-xs text-gray-500">Cycle</div>
                    </div>
                  </div>

                  {/* Badges */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    <Badge variant="outline" className={
                      group.status === 'active' ? 'text-green-600 border-green-200' : 'text-gray-600'
                    }>
                      {group.status === 'active' ? 'Actif' : group.status || 'Inactif'}
                    </Badge>
                    {group.category && (
                      <Badge variant="outline">{group.category}</Badge>
                    )}
                    {group.created_at && (
                      <Badge variant="outline" className="text-gray-500">
                        <Calendar className="w-3 h-3 mr-1" />
                        {new Date(group.created_at).toLocaleDateString('fr-FR')}
                      </Badge>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={() => setSelectedGroup(group)}
                      variant="outline"
                      className="flex items-center gap-2"
                    >
                      <Eye className="w-4 h-4" />
                      Voir détails
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => handleJoinRequest(group.id)}
                      className="flex items-center gap-2"
                    >
                      <UserPlus className="w-4 h-4" />
                      {isPrivate ? 'Demander l\'adhésion' : 'Rejoindre'}
                    </Button>
                  </div>
                </CardContent>
                </Card>
              </React.Fragment>
            );
          })
        )}
      </div>

      {/* Group Detail Modal */}
      {selectedGroup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <Card className="w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <div className="flex justify-between items-start">
                <CardTitle className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-gradient-to-r from-purple-400 to-blue-400 rounded-full flex items-center justify-center">
                    <span className="text-white font-bold">
                      {selectedGroup.name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  {selectedGroup.name}
                </CardTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedGroup(null)}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h4 className="font-medium mb-2">Description</h4>
                <p className="text-sm text-gray-600">
                  {selectedGroup.description || 'Aucune description disponible'}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="font-medium mb-1">Membres</h4>
                  <p className="text-2xl font-bold text-blue-600">
                    {selectedGroup.members?.length || 0}
                  </p>
                </div>
                <div>
                  <h4 className="font-medium mb-1">Contribution</h4>
                  <p className="text-2xl font-bold text-green-600">
                    {(selectedGroup.contribution_amount || 0).toLocaleString()} {selectedGroup.currency || 'KES'}
                  </p>
                </div>
              </div>

              {selectedGroup.admin_name && (
                <div>
                  <h4 className="font-medium mb-1">Administrateur</h4>
                  <p className="text-sm text-gray-600">{selectedGroup.admin_name}</p>
                </div>
              )}

              <div className="flex gap-2">
                <Button
                  onClick={() => {
                    handleJoinRequest(selectedGroup.id);
                    setSelectedGroup(null);
                  }}
                  className="flex-1 flex items-center justify-center gap-2"
                >
                  <UserPlus className="w-4 h-4" />
                  {selectedGroup.is_private ? 'Demander l\'adhésion' : 'Rejoindre ce groupe'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default ExploreGroupsPage;